import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search,
  Refresh,
  Download,
  TrendingUp,
  TrendingDown,
  Person,
  Receipt,
  CalendarToday,
  FilterList,
  Visibility,
  Star
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { API_URL } from './config';

const CustomerPerformancePanel = () => {
  const [tabValue, setTabValue] = useState(0);
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null
  });
  const [periodFilter, setPeriodFilter] = useState(7);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [customerDetails, setCustomerDetails] = useState(null);

  useEffect(() => {
    fetchAllCustomersPerformance();
  }, [periodFilter]);

  const fetchAllCustomersPerformance = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching customers performance for period:', periodFilter, 'days');
      
      const apiUrl = `${API_URL}/commission/analytics/customers-performance?days=${periodFilter}`;
      console.log('📡 API URL:', apiUrl);
      
      const response = await fetch(apiUrl);
      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON');
      }
      
      const data = await response.json();
      console.log('📊 API Response data:', data);
      
      if (data.status === 200) {
        console.log('✅ Success! Customers performance data:', data.customers);
        setPerformanceData(data.customers || []);
      } else {
        throw new Error(`API returned status: ${data.status}`);
      }
    } catch (error) {
      console.error('❌ Error fetching customers performance:', error);
      console.log('🔍 Error details:', {
        message: error.message,
        stack: error.stack,
        API_URL: API_URL
      });
      
      setPerformanceData([]);
      
      setAlert({
        show: true,
        type: 'error',
        message: 'Unable to load customer performance data. Please check if the backend API is implemented and running.'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      setLoading(true);
      console.log('🔍 Fetching details for customer:', customerId);
      
      const response = await fetch(`${API_URL}/commission/analytics/customers-performance/customer-details/${customerId}?days=30`);
      console.log('📡 Customer details response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON');
      }
      
      const data = await response.json();
      console.log('📊 Customer details data:', data);
      
      if (data.status === 200) {
        console.log('✅ Customer details loaded:', data.customer);
        setCustomerDetails(data.customer);
      } else {
        throw new Error(`API returned status: ${data.status}`);
      }
    } catch (error) {
      console.error('❌ Error fetching customer details:', error);
      console.log('🔍 Customer details error:', {
        message: error.message,
        customerId: customerId,
        API_URL: API_URL
      });
      
      setCustomerDetails(null);
      
      setAlert({
        show: true,
        type: 'error',
        message: 'Unable to load customer details. Please check if the backend API is implemented and running.'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const prepareChartData = () => {
    if (!customerDetails?.daily_breakdown || customerDetails.daily_breakdown.length === 0) {
      return [];
    }

    return customerDetails.daily_breakdown
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(item => ({
        date: formatDate(item.date),
        privateRides: parseFloat(item.private_rides_cost || 0),
        sharedRides: parseFloat(item.shared_rides_cost || 0),
        totalCost: parseFloat(item.total_cost || 0),
        ridesCount: (item.private_rides_count || 0) + (item.shared_rides_count || 0)
      }));
  };

  const prepareTopCustomersData = () => {
    if (!performanceData || performanceData.length === 0) {
      return [];
    }

    return performanceData
      .sort((a, b) => b.total_spent - a.total_spent)
      .slice(0, 10)
      .map(customer => ({
        name: customer.name || `Customer ${customer.id}`,
        totalSpent: parseFloat(customer.total_spent || 0),
        ridesCount: (customer.private_rides_count || 0) + (customer.shared_rides_count || 0),
        privateRides: customer.private_rides_count || 0,
        sharedRides: customer.shared_rides_count || 0
      }));
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredCustomers = performanceData.filter(customer =>
    (customer.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (customer.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    customer.id.toString().includes(searchTerm)
  );

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerDetails(customer.id);
    setTabValue(1); // Switch to details tab
  };

  const handleExportData = () => {
    const csvContent = [
      ['Customer ID', 'Name', 'Email', 'Total Rides', 'Private Rides', 'Shared Rides', 'Total Spent', 'Average Cost'],
      ...filteredCustomers.map(customer => [
        customer.id,
        customer.name || '',
        customer.email || '',
        (customer.private_rides_count || 0) + (customer.shared_rides_count || 0),
        customer.private_rides_count || 0,
        customer.shared_rides_count || 0,
        formatCurrency(customer.total_spent),
        formatCurrency(customer.average_cost_per_ride)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer_performance_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const renderSummaryCards = () => {
    const totalCustomers = performanceData.length;
    const totalSpent = performanceData.reduce((sum, customer) => sum + parseFloat(customer.total_spent || 0), 0);
    const totalRides = performanceData.reduce((sum, customer) => sum + (customer.private_rides_count || 0) + (customer.shared_rides_count || 0), 0);
    const totalPrivateRides = performanceData.reduce((sum, customer) => sum + (customer.private_rides_count || 0), 0);
    const totalSharedRides = performanceData.reduce((sum, customer) => sum + (customer.shared_rides_count || 0), 0);

    console.log('📊 Rendering customer summary cards:', {
      totalCustomers,
      totalSpent,
      totalRides,
      totalPrivateRides,
      totalSharedRides,
      performanceDataLength: performanceData.length,
      performanceData: performanceData
    });

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Person sx={{ mr: 1, color: '#1976d2' }} />
                <Typography variant="h6" color="#1976d2">
                  {totalCustomers}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Active Customers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Card sx={{ bgcolor: '#e8f5e8' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Receipt sx={{ mr: 1, color: '#2e7d32' }} />
                <Typography variant="h6" color="#2e7d32">
                  {formatCurrency(totalSpent)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Spent
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp sx={{ mr: 1, color: '#f57c00' }} />
                <Typography variant="h6" color="#f57c00">
                  {totalRides}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Rides
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Star sx={{ mr: 1, color: '#9c27b0' }} />
                <Typography variant="h6" color="#9c27b0">
                  {totalPrivateRides}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Private Rides
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Card sx={{ bgcolor: '#e8eaf6' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FilterList sx={{ mr: 1, color: '#3f51b5' }} />
                <Typography variant="h6" color="#3f51b5">
                  {totalSharedRides}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Shared Rides
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderCustomersTable = () => {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="600">
              Customer Performance Overview
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                }}
                sx={{ width: 250 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                  label="Period"
                >
                  <MenuItem value={7}>Last 7 Days</MenuItem>
                  <MenuItem value={14}>Last 14 Days</MenuItem>
                  <MenuItem value={30}>Last 30 Days</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchAllCustomersPerformance}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExportData}
              >
                Export
              </Button>
            </Box>
          </Box>

          {loading ? (
            <LinearProgress sx={{ mb: 2 }} />
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Total Rides</TableCell>
                    <TableCell align="right">Private</TableCell>
                    <TableCell align="right">Shared</TableCell>
                    <TableCell align="right">Total Spent</TableCell>
                    <TableCell align="right">Avg Cost/Ride</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCustomers.map((customer) => (
                    <TableRow key={customer.id} hover>
                      <TableCell>{customer.id}</TableCell>
                      <TableCell>{customer.name || `Customer ${customer.id}`}</TableCell>
                      <TableCell>{customer.email || 'N/A'}</TableCell>
                      <TableCell align="right">
                        {(customer.private_rides_count || 0) + (customer.shared_rides_count || 0)}
                      </TableCell>
                      <TableCell align="right">{customer.private_rides_count || 0}</TableCell>
                      <TableCell align="right">{customer.shared_rides_count || 0}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600" color="success.main">
                          {formatCurrency(customer.total_spent)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(customer.average_cost_per_ride)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewCustomer(customer)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderCustomerDetails = () => {
    if (!selectedCustomer || !customerDetails) {
      return (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" mb={2}>
                No Customer Data Available
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Please select a customer from the Overview tab to view detailed performance.
              </Typography>
              <Typography variant="body2" color="info.main" sx={{ fontStyle: 'italic' }}>
                Note: Backend API must be implemented to show real customer performance data.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const chartData = prepareChartData();

    return (
      <Box>
        {/* Customer Info Header */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" fontWeight="600">
                  {selectedCustomer.name || `Customer ${selectedCustomer.id}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedCustomer.email || 'N/A'} • ID: {selectedCustomer.id}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main" fontWeight="700">
                  {formatCurrency(customerDetails.total_spent)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Spent (30 days)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary.main" fontWeight="700">
                  {(customerDetails.private_rides_count || 0) + (customerDetails.shared_rides_count || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Rides
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="warning.main" fontWeight="700">
                  {customerDetails.private_rides_count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Private Rides
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="info.main" fontWeight="700">
                  {customerDetails.shared_rides_count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Shared Rides
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="600" mb={2}>
                  Spending Trend (Last 30 Days)
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="privateRides" stroke="#9c27b0" strokeWidth={2} name="Private Rides" />
                    <Line type="monotone" dataKey="sharedRides" stroke="#3f51b5" strokeWidth={2} name="Shared Rides" />
                    <Line type="monotone" dataKey="totalCost" stroke="#2e7d32" strokeWidth={2} name="Total Cost" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="600" mb={2}>
                  Ride Type Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[
                    { name: 'Private Rides', value: customerDetails.private_rides_count || 0, fill: '#9c27b0' },
                    { name: 'Shared Rides', value: customerDetails.shared_rides_count || 0, fill: '#3f51b5' }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="value" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderTopCustomers = () => {
    const topCustomers = prepareTopCustomersData();

    if (topCustomers.length === 0) {
      return (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" mb={2}>
                No Customer Performance Data Available
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Unable to load top customers data.
              </Typography>
              <Typography variant="body2" color="info.main" sx={{ fontStyle: 'italic' }}>
                Note: Backend API must be implemented to show real customer performance data.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="600" mb={3}>
            Top {topCustomers.length} Customers by Spending (Last {periodFilter} Days)
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={topCustomers} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={100} />
              <RechartsTooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="totalSpent" fill="#2e7d32" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Person sx={{ mr: 2, color: '#007bff' }} />
            <Typography variant="h4" fontWeight="600">
              Customer Performance Management
            </Typography>
          </Box>
        </Box>

        {/* Alert */}
        {alert.show && (
          <Alert 
            severity={alert.type} 
            sx={{ mb: 3 }}
            onClose={() => setAlert({ ...alert, show: false })}
          >
            {alert.message}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Overview" />
            <Tab label="Customer Details" />
            <Tab label="Top Customers" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {tabValue === 0 && (
          <Box>
            {renderSummaryCards()}
            {renderCustomersTable()}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            {renderCustomerDetails()}
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            {renderTopCustomers()}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default CustomerPerformancePanel;
